import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';

/**
 * The two pickers of manual commit ↔ task links (slice 2), one dialog:
 * "Link commit" on a task (options = the board's repository commits) and
 * "Link to task" on a commit (options = the project's tasks). A searchable
 * list (SearchableSelect, as in Move to…), Cancel and the action. `onPick`
 * may be async; the dialog closes when it resolves to anything but false.
 */
export default function PickLinkModal({
  title,
  options,
  getKey,
  getLabel,
  renderOption,
  placeholder,
  searchPlaceholder,
  emptyText,
  noMatchText,
  submitLabel,
  loading = false,
  onPick,
  onClose,
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const picked = options.find((o) => getKey(o) === choice) || null;

  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    try {
      const result = await onPick(picked);
      if (result !== false) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={title}>
      <div className="space-y-4" data-testid="pick-link-modal">
        <SearchableSelect
          options={options}
          value={choice}
          onChange={(key) => setChoice(key)}
          getKey={getKey}
          getLabel={getLabel}
          renderOption={renderOption}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          loading={loading}
          emptyText={emptyText}
          noMatchText={noMatchText}
        />
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!picked || busy}
            onClick={submit}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
